import type { ErrorResponse } from '@apollo/client/link/error'
import { captureException } from '@sentry/react'
import type { GraphQLFormattedError } from 'graphql'
import isNil from 'lodash/isNil'

import type { LutherException } from '@acre/graphql'

import ErrorProvider from '../errorHandler/ErrorProvider'
import { getStatusCode, isForbiddenError } from './links.helpers'

const isLutherException = (errors: readonly GraphQLFormattedError[]) => {
  return errors[0].extensions?.name === 'LutherException'
}

export const lutherErrorLink = ({ response, graphQLErrors, operation }: ErrorResponse) => {
  // We're not interested in Unauthorized errors, because those
  // are handled by the authRedirectErrorLink
  if (
    graphQLErrors?.length &&
    isLutherException(graphQLErrors) &&
    !graphQLErrors.some((error) => isForbiddenError(error, 'graphql'))
  ) {
    const graphQLError = graphQLErrors[0]
    const statusCode = getStatusCode(graphQLError, 'graphql')
    const error = (graphQLError as LutherException) || {}
    const link = document.createElement('a')
    link.href = error?.url

    error.message = `Request to ${link.pathname} failed with code ${statusCode}`

    // @ts-expect-error - Cypress is a global variable
    const isCypress = !isNil(window?.Cypress)
    const isPlaywright = navigator.webdriver
    if (!isCypress && !isPlaywright) {
      // Capture the error in Sentry with additional context
      captureException(error, {
        extra: {
          url: error?.url,
          status: error?.statusCode,
          message: error?.message,
        },
        contexts: {
          exception: {
            id: error?.id,
            type: error?.type,
            description: error?.description,
          },
          graphql: {
            operation: operation.operationName,
            variables: JSON.stringify(operation.variables, null, 2),
          },
        },
        tags: {
          ...(statusCode ? { status_code: statusCode } : {}),
        },
      })
    }
    console.error(graphQLErrors)
    if (response) response.errors = []

    // Prevent toast with error showing when there are insufficient permissions to get the source of ledgers
    // This permission issue should be fixed when these three BE ticekts are completed (at which point the if statement below can be removed):
    // https://acreplatforms.atlassian.net/browse/RD-8347
    // https://acreplatforms.atlassian.net/browse/RD-8349
    // https://acreplatforms.atlassian.net/browse/RD-8350
    if (
      graphQLError?.path?.includes('getLedgers') &&
      graphQLError?.path?.includes('sourceId') &&
      graphQLError?.message === 'Request failed with status code 403'
    ) {
      return
    }

    ErrorProvider.showError(graphQLError)
  }
}
